import { gt, isEmpty, isNumber } from 'lodash';
import { DATE_PART, DateObj, ERROR_TYPES } from './DatePickerTypes';
import {
  DATE_PICKER_WEEKS_FIVE,
  DATE_PICKER_WEEKS_SIX,
  DATE_PICKER_NUMBER_OF_MONTH,
  DATE_PICKER_FIVE_WEEK_GRID,
  DATE_PICKER_SIX_WEEK_GRID,
  DATE_PICKER_MAX_DATE_MONTH,
  DATE_PICKER_CONSTANTS
} from '../constants';
import { KeyboardEvent, FocusEvent } from 'react';
import { SUPPORTED_LOCALES, KEY_CHARS, DAYJS_DIFF } from '@trip-ux/constants';
import { dayjs } from '@trip-ux/common';

export const pickerClass = (cond: boolean): string => (cond ? 'closeIcon' : 'calendarIcon');

export const shiftDays = (
  requestedDate: Date,
  before: Date,
  after: Date,
  setFocusedDate: (date: DateObj) => void
): void => {
  const adjustedDate = new Date(requestedDate.getFullYear(), requestedDate.getMonth(), requestedDate.getDate());

  if (isDisabledDayBefore(adjustedDate, before)) {
    setFocusedDate(dateToObj(before));
  } else if (isDisabledDayAfter(adjustedDate, after)) {
    setFocusedDate(dateToObj(after));
  } else {
    setFocusedDate(dateToObj(requestedDate));
  }
};

export const validateDob = (newDate: DateObj, before: Date, after: Date): string | null => {
  if (isNumber(newDate.date) && gt(newDate.month, '-1') && isNumber(newDate.year)) {
    const digit = 2;
    const format2digit = (num: number) => `${num}`.padStart(digit, '0');

    const formattedDate = `${format2digit(newDate.month + 1)}-${format2digit(newDate.date)}-${newDate.year}`;
    const parsedDate = dayjs(formattedDate, 'MM-DD-YYYY', true);
    if (!parsedDate.isValid()) {
      return ERROR_TYPES.INVALID_DATE;
    }

    if (parsedDate.toDate() < before) {
      return ERROR_TYPES.MAX_AGE_ERROR;
    }

    if (isDisabledDay(parsedDate.toDate(), before, after)) {
      return ERROR_TYPES.RANGE_ERROR;
    }

    return null;
  }
  return ERROR_TYPES.INVALID_DATE;
};

export const blurCallback: (args: {
  event: FocusEvent<HTMLInputElement>;
  defaultValue: string;
  dateFormat: string;
  required: boolean;
  onError: (str: string) => void;
  before: Date;
  after: Date;
  selectDate: (e: FocusEvent<HTMLInputElement>, year: number, month: number, date: number) => void;
  onBlur: (e: FocusEvent<HTMLInputElement>) => void;
  deselectDate: (e: FocusEvent<HTMLInputElement>) => void;
}) => void = ({
  event,
  defaultValue,
  dateFormat,
  required,
  onError,
  before,
  after,
  selectDate,
  onBlur,
  deselectDate
}) => {
  onBlur(event);
  const parsedDate = dayjs(event.currentTarget.value, dateFormat, true);
  if ((event.currentTarget.value === defaultValue || isEmpty(event.currentTarget.value)) && required) {
    return onError(ERROR_TYPES.DATE_REQUIRED);
  }
  if (!parsedDate.isValid()) {
    return onError(ERROR_TYPES.INVALID_DATE);
  }
  if (parsedDate.toDate() < before) {
    return onError(ERROR_TYPES.MAX_AGE_ERROR);
  }
  if (isDisabledDay(parsedDate.toDate(), before, after)) {
    return onError(ERROR_TYPES.RANGE_ERROR);
  }
  if (event.currentTarget.value) {
    return selectDate(event, parsedDate.year(), parsedDate.month(), parsedDate.date());
  }
  return deselectDate(event);
};

export const checkOrDefaultDayjs = (defaultValue: string, format: string): dayjs.Dayjs =>
  defaultValue ? dayjs(defaultValue, format) : dayjs();

export const checkOrDefault = (
  defaultValue: string,
  format: string,
  defaultDate: null | DateObj = null
): DateObj | null => {
  const parsedDate = checkOrDefaultDayjs(defaultValue, format);
  return defaultValue
    ? {
        year: parsedDate.year(),
        month: parsedDate.month(),
        date: parsedDate.date()
      }
    : defaultDate;
};

export const isEqual = (a: DateObj, b: DateObj): boolean =>
  a && b && b.year === a.year && b.month === a.month && b.date === a.date;

export const isEqualDayjs = (a: DateObj, b: dayjs.Dayjs): boolean =>
  a && b && b.year() === a.year && b.month() === a.month && b.date() === a.date;

export const classCheck = (cond: boolean, className: string): string => (cond ? className : '');

export const getWeeks = (days: number): number[] =>
  days > DATE_PICKER_FIVE_WEEK_GRID ? DATE_PICKER_WEEKS_SIX : DATE_PICKER_WEEKS_FIVE;

export const dateToObj = (date: Date): DateObj => ({
  year: date.getFullYear(),
  month: date.getMonth(),
  date: date.getDate()
});

export const shiftDate = (currentDate: DateObj, offset: number, type: DATE_PART, isFirstOfMonth = false): Date => {
  const { year, month, date } = currentDate;
  switch (type) {
    case DATE_PART.YEAR: {
      return new Date(year + offset, month, isFirstOfMonth ? 1 : date);
    }
    case DATE_PART.MONTH: {
      return new Date(year, month + offset, isFirstOfMonth ? 1 : date);
    }
    case DATE_PART.DAY:
    default: {
      return new Date(year, month, date + offset);
    }
  }
};

export const clampMonth = (month: number, adj: number): number =>
  month + adj >= 0
    ? (month + adj) % DATE_PICKER_NUMBER_OF_MONTH
    : ((month + adj) % DATE_PICKER_NUMBER_OF_MONTH) + DATE_PICKER_NUMBER_OF_MONTH;

export const getLastDayOfMonth = (year: number, month: number): number => new Date(year, month + 1, 0).getDate();

export const isDisabledDayBefore = (adjustedDate: Date, before: Date) => {
  if (!before) {
    return false;
  }
  const adjustedBeforeDate = new Date(before.getFullYear(), before.getMonth(), before.getDate());
  if (dayjs(adjustedDate).isBefore(adjustedBeforeDate, DAYJS_DIFF.DAYS)) {
    return true;
  }
  return false;
};

export const isDisabledDayAfter = (adjustedDate: Date, after: Date) => {
  if (!after) {
    return false;
  }
  const adjustedAfterDate = new Date(after.getFullYear(), after.getMonth(), after.getDate());
  if (dayjs(adjustedDate).isAfter(adjustedAfterDate, DAYJS_DIFF.DAYS)) {
    return true;
  }
  return false;
};

export const isDisabledDay = (date: Date, before: Date, after: Date): boolean => {
  if (!date) {
    return false;
  }
  const adjustedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (isDisabledDayBefore(adjustedDate, before)) {
    return true;
  }
  if (isDisabledDayAfter(adjustedDate, after)) {
    return true;
  }
  return false;
};

export const isDisabledMonth = (date: Date, before: Date, after: Date, offset: number): boolean => {
  if (!date) {
    return false;
  }
  const adjustedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (before) {
    const adjustedBeforeDate = new Date(before.getFullYear(), before.getMonth(), 1);
    if (dayjs(adjustedDate).isBefore(adjustedBeforeDate, DAYJS_DIFF.DAYS) && offset < 0) {
      return true;
    }
  }
  if (after) {
    const adjustedAfterDate = new Date(
      after.getFullYear(),
      after.getMonth(),
      getLastDayOfMonth(after.getFullYear(), after.getMonth())
    );
    if (dayjs(adjustedDate).isAfter(adjustedAfterDate, DAYJS_DIFF.DAYS) && offset > 0) {
      return true;
    }
  }
  return false;
};

export const genDaysOfMonth = (
  year: number,
  month: number,
  disabledDays: {
    before?: Date;
    after?: Date;
  } = {}
): {
  date: number;
  year: number;
  month: number;
  isDisabled?: boolean;
}[] => {
  const date = new Date(year, month, 1);
  const { before, after } = disabledDays;
  const daysOfMonth = [];
  const dayOfWeek = date.getDay();

  const lastMonth = clampMonth(month, -1);
  const lastMonthYear = month - 1 < 0 ? year - 1 : year;
  const nextMonth = clampMonth(month, +1);
  const nextMonthYear = month + 1 > DATE_PICKER_MAX_DATE_MONTH ? year + 1 : year;

  const daysInMonthPrev = new Date(year, month, 0).getDate();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const totalDays =
    daysInMonth + dayOfWeek >= DATE_PICKER_FIVE_WEEK_GRID ? DATE_PICKER_SIX_WEEK_GRID : DATE_PICKER_FIVE_WEEK_GRID;

  for (let i = dayOfWeek; i > 0; i -= 1) {
    const day = {
      month: lastMonth,
      date: daysInMonthPrev - i + 1,
      year: lastMonthYear
    };
    const newDate = new Date(day.year, day.month, day.date);
    daysOfMonth.push({
      ...day,
      isDisabled: isDisabledDay(newDate, before, after)
    });
  }
  for (let i = 1; i <= daysInMonth; i += 1) {
    const day = {
      date: i,
      month,
      year
    };
    const newDate = new Date(day.year, day.month, day.date);
    daysOfMonth.push({
      ...day,
      isDisabled: isDisabledDay(newDate, before, after)
    });
  }
  const daysLeft = totalDays - daysInMonth - dayOfWeek;
  for (let i = 1; i <= daysLeft; i += 1) {
    const day = {
      month: nextMonth,
      date: i,
      year: nextMonthYear
    };
    const newDate = new Date(day.year, day.month, day.date);
    daysOfMonth.push({
      ...day,
      isDisabled: isDisabledDay(newDate, before, after)
    });
  }
  return daysOfMonth;
};

export const getMonths = (language: SUPPORTED_LOCALES): string[] => {
  dayjs.locale(language);
  return dayjs.months();
};

export const getWeekdays = (language: SUPPORTED_LOCALES): { abbr: string; title: string }[] => {
  dayjs.locale(language);
  return dayjs.weekdays().map((name, idx) => ({
    abbr: dayjs.weekdaysMin()[idx],
    title: name
  }));
};

export const buildHandler = (
  event: KeyboardEvent,
  shift: (unit: number) => void,
  selectDate: (e: KeyboardEvent) => void
): void => {
  const { key } = event;
  switch (key) {
    case KEY_CHARS.ARROW_LEFT: {
      event.preventDefault();
      shift(DATE_PICKER_CONSTANTS.REDUCE_DAY);
      break;
    }
    case KEY_CHARS.ARROW_RIGHT: {
      event.preventDefault();
      shift(DATE_PICKER_CONSTANTS.ADD_DAY);
      break;
    }
    case KEY_CHARS.ARROW_UP: {
      event.preventDefault();
      shift(DATE_PICKER_CONSTANTS.REDUCE_WEEK);
      break;
    }
    case KEY_CHARS.ARROW_DOWN: {
      event.preventDefault();
      shift(DATE_PICKER_CONSTANTS.ADD_WEEK);
      break;
    }
    case KEY_CHARS.ENTER: {
      selectDate(event);
      break;
    }
    default: {
      break;
    }
  }
};
